import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { CustomerI } from '@interfaces/customer.interface';
import { PageI } from '@interfaces/page.interface';
import { CustomerSettingsI } from '@interfaces/customer-settings.interface';
import { BehaviorSubject, distinctUntilChanged, map, switchMap, tap, Observable } from '@node_modules/rxjs';

@Injectable({
  providedIn: 'root',
})
export class CustomersService {
  constructor(private http: HttpClient) {}

  private baseUrl = environment.OMNISMART_API_CUSTOMER;
  private slug_url = 'customers';
  private currentCustomerSubject = new BehaviorSubject<CustomerI | null>(null);
  public currentCustomer$ = this.currentCustomerSubject.asObservable();
  public useModulePabx$ = this.getCurrentCustomer().pipe(
    map((customer) => {
      return customer.useModulePabx ?? false;
    }),
    distinctUntilChanged()
  );

  public createANewCustomer(customerData: CustomerI): Observable<CustomerI> {
    return this.http.post<CustomerI>(
      `
      ${this.baseUrl}${this.slug_url}`,
      customerData
    );
  }

  public getCustomers(
    search: any = {},
    sort = 'desc',
    page = 1,
    limit = 999,
    active = 'createdAt'
  ): Observable<PageI<CustomerI>> {
    let params = new HttpParams()
      .set('sort', sort)
      .set('active', active)
      .set('limit', limit.toString())
      .set('page', page.toString());
    for (let key in search) {
      const value = search[key];
      if (value) {
        params = params.set(key, value);
      }
    }
    return this.http.get<PageI<CustomerI>>(`${this.baseUrl}${this.slug_url}`, {
      params,
    });
  }

  public getCountCustomers(): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}${this.slug_url}/count`);
  }

  public partialUpdate(id: string, customerData: Partial<CustomerI>): Observable<CustomerI> {
    return this.http.patch<CustomerI>(`${this.baseUrl}${this.slug_url}/${id}`, customerData);
  }

  public updateWebhook(id: string, customerData: Partial<CustomerI>): Observable<CustomerI> {
    return this.http.patch<CustomerI>(`${this.baseUrl}${this.slug_url}/webhook/${id}`, customerData);
  }

  public enableSendSMS(id: string): Observable<CustomerI> {
    return this.http.post<CustomerI>(`${this.baseUrl}${this.slug_url}/change-sms`, { id });
  }

  public getTokenJWT(id: string): Observable<CustomerI> {
    return this.http.get<any>(`${this.baseUrl}${this.slug_url}/jwt-token/${id}`);
  }

  public getCustomerById(id: string): Observable<CustomerI> {
    return this.http.get<any>(`${this.baseUrl}${this.slug_url}/${id}`);
  }

  public getcustomerSettingsById(id: string): Observable<CustomerSettingsI> {
    return this.http.get<Partial<CustomerSettingsI>>(`${this.baseUrl}${this.slug_url}/modules/${id}`);
  }

  public getCustomerLogoByUrl(url: string): Observable<Partial<CustomerSettingsI>> {
    return this.http.get<Partial<CustomerSettingsI>>(`${this.baseUrl}${this.slug_url}/logo/${url}`);
  }

  public getCurrentCustomer(): Observable<CustomerI> {
    return this.currentCustomer$.pipe(
      switchMap((value) => {
        if (!value) {
          return this.http
            .get<CustomerI>(`${this.baseUrl}${this.slug_url}/current`)
            .pipe(tap((value: CustomerI) => this.currentCustomerSubject.next(value)));
        }
        return this.currentCustomer$ as Observable<CustomerI>;
      })
    );
  }

  public invalidateCache() {
    this.currentCustomerSubject.next(null);
  }

  public getAddressByCep(cep: string): Observable<any> {
    return this.http.get<any>(`https://viacep.com.br/ws/${cep}/json/`, {
      headers: {
        'content-type': 'application/json;charset=utf-8',
      },
    });
  }

  public deleteCustomer(id: string) {
    return this.http.delete(`${this.baseUrl}${this.slug_url}/${id}`);
  }

  public generateApiTokenCustomer(id: string) {
    return this.http.post(`${this.baseUrl}${this.slug_url}/generate-api`, { id });
  }

  public getAllowedTemplateTypes(): Observable<string[]> {
    return this.http.get<string[]>(`${this.baseUrl}${this.slug_url}/templates`);
  }
}
